import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ChevronRight, CheckCircle2 } from "lucide-react";
import { formatCurrency } from "@/lib/format";

/**
 * Outstanding payments — hero amount + light breakdown.
 * NO decorative colors; only subtle rose tint if overdue exists.
 */
export default function OutstandingPanel({ overview, currency, buckets }) {
    const { t } = useTranslation();
    const total = overview?.pending_payments_amount || 0;
    const count = overview?.pending_payments_count || 0;

    if (total === 0) {
        return (
            <section className="rounded-[10px] border border-border bg-card p-5" data-testid="outstanding-card">
                <h2 className="text-[15px] font-semibold text-foreground">
                    {t("dashboard.outstanding_payments")}
                </h2>
                <div className="mt-3 flex items-center gap-2 text-[13px] text-emerald-700">
                    <CheckCircle2 size={15} className="text-emerald-500" />
                    {t("dashboard.no_outstanding")}
                </div>
            </section>
        );
    }

    const rows = [
        { key: "overdue",   label: t("dashboard.overdue"),   value: buckets?.overdue  || 0, tone: "rose" },
        { key: "due_today", label: t("dashboard.due_today"), value: buckets?.dueToday || 0, tone: "amber" },
        { key: "upcoming",  label: t("dashboard.upcoming"),  value: buckets?.upcoming || 0, tone: "slate" },
    ];

    const dotClass = { rose: "bg-rose-500", amber: "bg-amber-500", slate: "bg-slate-400" };

    return (
        <section className="rounded-[10px] border border-border bg-card" data-testid="outstanding-card">
            <div className="px-5 pt-4">
                <h2 className="text-[15px] font-semibold text-foreground">
                    {t("dashboard.outstanding_payments")}
                </h2>
                <div className="mt-3 text-[28px] font-semibold leading-none tracking-tight text-foreground tabular-nums">
                    {formatCurrency(total, currency)}
                </div>
                <div className="mt-1.5 text-[12px] text-muted-foreground">
                    {t("dashboard.n_reservations", { count })}
                </div>
            </div>

            <ul className="mt-3 px-5">
                {rows.map((r) => (
                    <li key={r.key}
                        className="flex items-center justify-between border-t border-border/60 py-2 text-[13px]">
                        <span className="flex items-center gap-2 text-foreground/90">
                            <span className={`inline-flex h-1.5 w-1.5 rounded-full ${dotClass[r.tone]}`} />
                            {r.label}
                        </span>
                        <span className="font-medium tabular-nums text-foreground">
                            {formatCurrency(r.value, currency)}
                        </span>
                    </li>
                ))}
            </ul>

            <div className="px-5 py-3">
                <Link
                    to="/billing"
                    className="inline-flex items-center gap-1 text-[12.5px] font-medium text-[#ff7b02] hover:underline"
                    data-testid="outstanding-link"
                >
                    {t("dashboard.view_payments")}
                    <ChevronRight size={13} />
                </Link>
            </div>
        </section>
    );
}
